import { Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { OrderAddress } from "src/services/order/order.interface";
import { google } from "google-maps";

declare let google: any;

export type GoogleMapsPlace = google.maps.places.PlaceResult;

export type GoogleMapsAutocompleteResult = google.maps.places.AutocompletePrediction;

@Injectable({
  providedIn: "root",
})
export class GoogleMapsService {
  private readonly googleAutocompleteApi = new google.maps.places.AutocompleteService();
  private readonly googlePlacesApi: google.maps.places.PlacesService;

  constructor() {
    const fakeMap = new google.maps.Map(document.createElement("div"));
    this.googlePlacesApi = new google.maps.places.PlacesService(fakeMap);
  }

  public async autocomplete(query: string): Promise<GoogleMapsAutocompleteResult[]> {
    return (await this.googleAutocompleteApi.getPlacePredictions({ input: query }))?.predictions;
  }

  public getPlaceDetails(placeId: string, callback: (place: GoogleMapsPlace) => void | Promise<void>): void {
    const completePlaceRequest = {
      placeId,
      fields: [
        "address_components",
        "name",
        "formatted_phone_number",
        "geometry",
        "url",
        "formatted_address",
        "website",
      ],
    };

    this.googlePlacesApi.getDetails(completePlaceRequest, async (place: GoogleMapsPlace, status: any) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        await callback(place);
      }
    });
  }

  public placeToAddress(place: GoogleMapsPlace): OrderAddress {
    const location = place.geometry?.location;

    if (!location) {
      throw new Error("The selected place has no coordinates.");
    }

    return {
      country: this.findValueOfAddressComponentOfType("country", place.address_components),
      formatted_address: place.formatted_address ?? "",
      id: place.place_id,
      lat: location.lat(),
      lng: location.lng(),
      name: place.name,
      placeId: place.place_id ?? "",
      street: this.findValueOfAddressComponentOfType("route", place.address_components) ?? "-",
      state: this.findValueOfAddressComponentOfType("administrative_area_level_1", place.address_components),
      zipCode: this.findValueOfAddressComponentOfType("postal_code", place.address_components),
    };
  }

  public generateStaticMapUrlFromCoordinates(latitude: string | number, longitude: string | number, zoom: number = 12) {
    const url = this.generateStaticMapUrlInternal(zoom);
    url.searchParams.append("center", `${latitude},${longitude}`);
    url.searchParams.append("markers", `size:mid|color:gray|${latitude},${longitude}`);

    return url.toString();
  }

  public generateStaticMapUrlWithoutMarker(latitude: string | number, longitude: string | number, zoom: number = 12) {
    const url = this.generateStaticMapUrlInternal(zoom);
    url.searchParams.append("center", `${latitude},${longitude}`);

    return url.toString();
  }

  private generateStaticMapUrlInternal(zoom: number = 12): URL {
    const rootUrl = "https://maps.googleapis.com/maps/api/staticmap";

    let url = new URL(rootUrl);
    url.searchParams.append("format", "png");
    url.searchParams.append("key", environment.firebase.apiKey);
    url.searchParams.append("maptype", "roadmap");
    url.searchParams.append("scale", "2");
    url.searchParams.append("size", "700x350");
    url.searchParams.append("zoom", String(zoom));

    url = this.styleMap(url);

    return url;
  }

  private styleMap(input: URL): URL {
    const blue = "0xa9c8e0";
    const green = "0xcce7a8";
    const grey = "0xd8e3e6";
    const white = "0xffffff";

    input.searchParams.append("style", "feature:poi|visibility:off");
    input.searchParams.append("style", `feature:poi.park|element:geometry.fill|color:${green}|visibility:simplified`);
    input.searchParams.append("style", `feature:landscape.natural|element:geometry.fill|color:${green}`);
    input.searchParams.append("style", `feature:landscape.man_made|element:geometry.fill|color:${grey}`);
    input.searchParams.append("style", `feature:road|element:geometry|color:${white}`);
    input.searchParams.append("style", `feature:water|color:${blue}`);
    input.searchParams.append("style", "feature:transit|visibility:on");
    input.searchParams.append("style", "feature:poi.attraction|visibility:on");
    input.searchParams.append("style", "feature:poi.sports_complex|visibility:on");

    return input;
  }

  private findValueOfAddressComponentOfType(
    type: string,
    addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
  ): string {
    for (const component of addressComponents ?? []) {
      if (component.types.includes(type)) {
        return component.long_name;
      }
    }

    return "";
  }
}
